import { getAnalyticsRequestId } from 'utils/analyticsRequestId'
import sendTrackerLog from 'utils/tracker'
import Cookies from 'js-cookie'

// Helper function to get anonymous_id
const getAnonymousId = (): string | null => {
  if (globalThis.window === undefined) return null

  // 1. Check query param
  const urlParams = new URLSearchParams(globalThis.window.location.search)
  const queryAnonymousId = urlParams.get('anonymous_id')
  if (queryAnonymousId) return queryAnonymousId

  // 2. Check cookie using Cookies.get (case insensitive)
  const cciCookie = Cookies.get('CCI') || Cookies.get('cci')
  if (cciCookie) return cciCookie

  // 3. Fallback: Check cookie string directly
  const cookieString = document.cookie
  const match = /(^|;\s*)CCI=([^;]+)/i.exec(cookieString)
  if (match) {
    return match[2] || null
  }

  return null
}

// Helper function to get session_id
const getSessionId = (): string | null => {
  if (globalThis.window === undefined) return null

  // Try common session cookie names
  const sessionId = Cookies.get('session_id') || Cookies.get('SESSIONID') || Cookies.get('sessionId') || Cookies.get('session') || Cookies.get('SESSION') || null
  return sessionId
}

const getUserIdFromAuthUser = (user: any): string | null => {
  if (!user) return null
  if ('userId' in user && user.userId) return String(user.userId)
  if ('id' in user && user.id) return String(user.id)
  return null
}

const getUserIdFromCookie = (): string | null => {
  if (globalThis.window === undefined) return null
  return Cookies.get('userId') || Cookies.get('user_id') || Cookies.get('USERID') || null
}

const getUserIdFromPreloadedState = (): string | null => {
  if (globalThis.window === undefined || !(globalThis as any).__PRELOADED_STATE__) return null
  const preloadedUser = (globalThis as any).__PRELOADED_STATE__.user
  if (!preloadedUser) return null
  if (preloadedUser.userId) return String(preloadedUser.userId)
  if (preloadedUser.id) return String(preloadedUser.id)
  return null
}

// Helper function to get user_id from auth context
const getUserId = (authContext: any): string | null => {
  const fromAuth = getUserIdFromAuthUser(authContext?.user)
  if (fromAuth) return fromAuth

  const fromCookie = getUserIdFromCookie()
  if (fromCookie) return fromCookie

  return getUserIdFromPreloadedState()
}

// Helper function to get domain from hostname
const getDomain = (): string => {
  if (globalThis.window === undefined) return 'enuygun'
  const hostname = globalThis.window.location.hostname
  if (hostname.includes('wingie')) return 'wingie'
  return 'enuygun'
}

// Helper function to get environment
const getEnvironment = (isMobile: boolean): string => {
  return isMobile ? 'mobile_web' : 'desktop'
}

// Helper function to get page path
const getPagePath = (): string => {
  if (globalThis.window === undefined) return '/'
  return globalThis.window.location.pathname
}

// Helper function to get language
const getLanguage = (i18nLanguage?: string): string => {
  if (!i18nLanguage) return 'tr'
  // Extract language code (e.g., 'tr-TR' -> 'tr')
  return i18nLanguage.split('-')[0] || 'tr'
}

// Reco List View Event
export const logRecoListView = (params: { flights: any[]; title: string; isMobile: boolean; authContext: any; language?: string }) => {
  const { flights, title, isMobile, authContext, language } = params

  if (!flights || flights.length === 0) return

  const anonymousId = getAnonymousId()
  const sessionId = getSessionId()
  const userId = getUserId(authContext)
  const domain = getDomain()
  const environment = getEnvironment(isMobile)
  const pagePath = getPagePath()
  const lang = getLanguage(language)

  const requestId = getAnalyticsRequestId()

  sendTrackerLog({
    category: 'reco',
    page: pagePath,
    type: 'reco_list_view',
    message: `reco_list_view|placement_id=enuygun-homepage-flight-reco|items_count=${flights.length}`,
    environment: environment,
    requestId,
    ctxt_type: 'reco_list_view',
    ctxt_category: 'reco',
    event_timestamp: new Date().toISOString(),
    raw_payload: JSON.stringify({
      flights: flights.map((flight, index) => ({
        index,
        id: flight.id || `flight-${index}`,
        origin: flight.origin?.cityName || flight.search_parameters?.origin?.city_name,
        destination: flight.destination?.cityName || flight.search_parameters?.destination?.city_name,
      })),
    }),
    product_type: 'flight',
    component_type: 'reco_slider',
    placement_id: 'enuygun-homepage-flight-reco',
    placement_title: title,
    list_position: 1,
    items_sent_count: flights.length,
    user_id: userId,
    anonymous_id: anonymousId,
    session_id: sessionId,
    page_path: pagePath,
    domain: domain,
    language: lang,
  })
}

// Reco Item View Event
export const logRecoItemView = (params: {
  flight: any
  itemPosition: number
  listPosition: number
  totalItems: number
  isMobile: boolean
  authContext: any
  language?: string
}) => {
  const { flight, itemPosition, listPosition, totalItems, isMobile, authContext, language } = params

  if (!flight) return

  const anonymousId = getAnonymousId()
  const sessionId = getSessionId()
  const userId = getUserId(authContext)
  const domain = getDomain()
  const environment = getEnvironment(isMobile)
  const pagePath = getPagePath()
  const lang = getLanguage(language)

  // Extract flight data
  const itemId = flight.id || `flight-${itemPosition}`
  const airline = flight.airline || flight.search_parameters?.provider || null
  const departureAirport = flight.origin?.code || flight.search_parameters?.origin?.city_code || null
  const arrivalAirport = flight.destination?.code || flight.search_parameters?.destination?.city_code || null
  const isDirect = flight.isDirect === undefined ? flight.search_parameters?.is_direct || null : flight.isDirect
  const listingId = flight.listing_id || flight.search_parameters?.request_id || null

  const requestId = getAnalyticsRequestId()

  sendTrackerLog({
    category: 'reco',
    page: pagePath,
    type: 'reco_item_view',
    message: `reco_item_view|item_id=${itemId}|item_position=${itemPosition}`,
    environment: environment,
    requestId,
    ctxt_type: 'reco_item_view',
    ctxt_category: 'reco',
    event_timestamp: new Date().toISOString(),
    raw_payload: JSON.stringify({
      flight: {
        id: itemId,
        origin: flight.origin?.cityName || flight.search_parameters?.origin?.city_name,
        destination: flight.destination?.cityName || flight.search_parameters?.destination?.city_name,
        airline,
        departureAirport,
        arrivalAirport,
        isDirect,
      },
    }),
    product_type: 'flight',
    component_type: 'reco_slider',
    placement_id: 'enuygun-homepage-flight-reco',
    list_position: listPosition,
    item_position: itemPosition,
    items_sent_count: totalItems,
    item_id: itemId,
    airline: airline,
    departure_airport: departureAirport,
    arrival_airport: arrivalAirport,
    is_direct: isDirect,
    listing_id: listingId,
    user_id: userId,
    anonymous_id: anonymousId,
    session_id: sessionId,
    page_path: pagePath,
    domain: domain,
    language: lang,
  })
}

// Reco Item Click Event
export const logRecoItemClick = (params: {
  flight: any
  itemPosition: number
  listPosition: number
  totalItems: number
  isMobile: boolean
  authContext: any
  language?: string
}) => {
  const { flight, itemPosition, listPosition, totalItems, isMobile, authContext, language } = params

  if (!flight) return

  const anonymousId = getAnonymousId()
  const sessionId = getSessionId()
  const userId = getUserId(authContext)
  const domain = getDomain()
  const environment = getEnvironment(isMobile)
  const pagePath = getPagePath()
  const lang = getLanguage(language)

  // Extract flight data
  const itemId = flight.id || `flight-${itemPosition}`
  const airline = flight.airline || flight.search_parameters?.provider || null
  const departureAirport = flight.origin?.code || flight.search_parameters?.origin?.city_code || null
  const arrivalAirport = flight.destination?.code || flight.search_parameters?.destination?.city_code || null
  const isDirect = flight.isDirect === undefined ? flight.search_parameters?.is_direct || null : flight.isDirect
  const listingId = flight.listing_id || flight.search_parameters?.request_id || null

  const requestId = getAnalyticsRequestId()

  sendTrackerLog({
    category: 'reco',
    page: pagePath,
    type: 'reco_item_click',
    message: `reco_item_click|item_id=${itemId}|item_position=${itemPosition}`,
    environment: environment,
    requestId,
    ctxt_type: 'reco_item_click',
    ctxt_category: 'reco',
    event_timestamp: new Date().toISOString(),
    raw_payload: JSON.stringify({
      flight: {
        id: itemId,
        origin: flight.origin?.cityName || flight.search_parameters?.origin?.city_name,
        destination: flight.destination?.cityName || flight.search_parameters?.destination?.city_name,
        airline,
        departureAirport,
        arrivalAirport,
        isDirect,
        searchUrl: flight.searchUrl || flight.search_url,
      },
    }),
    product_type: 'flight',
    component_type: 'reco_slider',
    placement_id: 'enuygun-homepage-flight-reco',
    list_position: listPosition,
    item_position: itemPosition,
    items_sent_count: totalItems,
    item_id: itemId,
    airline: airline,
    departure_airport: departureAirport,
    arrival_airport: arrivalAirport,
    is_direct: isDirect,
    listing_id: listingId,
    user_id: userId,
    anonymous_id: anonymousId,
    session_id: sessionId,
    page_path: pagePath,
    domain: domain,
    language: lang,
  })
}
